/**
 * Phase 20.12 + iter_212 — Notification Settings
 *
 * Two sections:
 *   1. Categories (iter_212) — in-app + push toggles per user-visible
 *      notification category. Required categories (billing / identity)
 *      are visible but locked ON with a small note.
 *   2. Email (legacy Phase 20.12) — the original single beat_sale_email
 *      toggle kept for backwards compatibility.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import SharedNavigation from "@/components/SharedNavigation";
import { useCurrentUser } from "@/lib/useCurrentUser";

const EMAIL_TOGGLES = [
  {
    key: "beat_sale_email",
    label: "Beat Sale Email",
    description:
      "Send me an email the moment a buyer licenses one of my beats. Always includes title, tier, gross + net amount, and a link to my Producer Dashboard.",
  },
];

const Toggle = ({ on, onClick, disabled, testId, locked }) => (
  <button
    onClick={onClick}
    disabled={disabled || locked}
    data-testid={testId}
    aria-pressed={on}
    title={locked ? "This category is required and cannot be turned off." : undefined}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
      on ? "bg-emerald-500" : "bg-gray-600"
    } ${locked ? "opacity-70 cursor-not-allowed" : ""} disabled:opacity-50`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
        on ? "translate-x-6" : "translate-x-1"
      }`}
    />
  </button>
);

const NotificationSettingsPage = () => {
  const { currentUser } = useCurrentUser();

  // Categories (iter_212)
  const [catData, setCatData] = useState(null);
  const [catLoading, setCatLoading] = useState(true);
  const [catSaving, setCatSaving] = useState(false);

  // Legacy email prefs (Phase 20.12)
  const [emailPrefs, setEmailPrefs] = useState({});
  const [emailLoading, setEmailLoading] = useState(true);
  const [emailSavingKey, setEmailSavingKey] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/me/notification-preferences");
        if (!cancelled) setCatData(r.data || null);
      } catch (err) {
        if (err?.response?.status === 401) toast.error("Sign in to manage settings");
        else toast.error("Could not load notification preferences");
      } finally {
        if (!cancelled) setCatLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/users/me/notification-prefs");
        if (!cancelled) setEmailPrefs(r.data?.prefs || {});
      } catch {
        // silent — legacy endpoint is optional here
      } finally {
        if (!cancelled) setEmailLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const persistCategories = async (nextPrefs) => {
    setCatSaving(true);
    try {
      const r = await api.put("/me/notification-preferences", { prefs: nextPrefs });
      setCatData(r.data);
      toast.success("Notification preferences saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
      // Re-fetch to sync
      try {
        const r = await api.get("/me/notification-preferences");
        setCatData(r.data);
      } catch (_e) { /* ignore */ }
    } finally {
      setCatSaving(false);
    }
  };

  const toggleCategoryChannel = (catKey, channel, locked) => {
    if (!catData || locked) return;
    const current = catData.prefs?.[catKey] || { in_app: true, push: true };
    const next = { ...current, [channel]: !current[channel] };
    const nextPrefs = { ...(catData.prefs || {}), [catKey]: next };
    setCatData({ ...catData, prefs: nextPrefs }); // optimistic
    persistCategories(nextPrefs);
  };

  const toggleEmailPref = async (key) => {
    const next = !emailPrefs[key];
    setEmailSavingKey(key);
    setEmailPrefs((p) => ({ ...p, [key]: next }));
    try {
      const r = await api.patch("/users/me/notification-prefs", { [key]: next });
      setEmailPrefs(r.data?.prefs || emailPrefs);
      toast.success(next ? "Email notifications ON" : "Email notifications OFF");
    } catch (err) {
      setEmailPrefs((p) => ({ ...p, [key]: !next }));
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setEmailSavingKey(null);
    }
  };

  const categories = catData?.categories || [];
  const catPrefs = catData?.prefs || {};

  return (
    <div className="min-h-screen bg-[#08080c]" data-testid="notification-settings-page">
      <SharedNavigation currentUser={currentUser} />
      <div className="pt-24 pb-24 px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto">
        <header className="mb-8">
          <p className="text-[11px] uppercase tracking-widest text-purple-300 font-bold">Settings</p>
          <h1 className="text-3xl sm:text-4xl font-black text-white">🔔 Notifications</h1>
          <p className="text-sm text-gray-400 mt-1 max-w-2xl">
            Choose which categories reach your bell. Required categories
            (billing, identity) stay on so you never miss critical account
            updates.
          </p>
        </header>

        {/* Category toggles (iter_212) */}
        <section className="mb-10" data-testid="notif-category-section">
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-lg font-black text-white">Categories</h2>
            {catSaving && <span className="text-[10px] text-gray-400">Saving…</span>}
          </div>
          {catLoading ? (
            <div className="text-center text-gray-400 py-8" data-testid="notif-category-loading">Loading…</div>
          ) : (
            <div className="space-y-2" data-testid="notif-category-list">
              <div className="grid grid-cols-[1fr_60px_60px] gap-2 px-4 pb-1 text-[10px] uppercase tracking-widest text-gray-500">
                <div>Category</div>
                <div className="text-center">In-app</div>
                <div className="text-center">Push</div>
              </div>
              {categories.map((c) => {
                const p = catPrefs[c.key] || { in_app: true, push: true };
                const locked = !!c.required;
                return (
                  <div
                    key={c.key}
                    className="grid grid-cols-[1fr_60px_60px] gap-2 items-center p-4 rounded-2xl bg-[#11111a] border border-purple-500/20"
                    data-testid={`notif-cat-row-${c.key}`}
                  >
                    <div className="min-w-0">
                      <p className="text-white font-bold text-sm flex items-center gap-2">
                        {c.label}
                        {locked && (
                          <span
                            className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-200 border border-amber-500/40"
                            data-testid={`notif-cat-required-${c.key}`}
                          >
                            Required
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">{c.description}</p>
                    </div>
                    <div className="flex justify-center">
                      <Toggle
                        on={locked ? true : !!p.in_app}
                        locked={locked}
                        onClick={() => toggleCategoryChannel(c.key, "in_app", locked)}
                        disabled={catSaving}
                        testId={`notif-cat-in_app-${c.key}`}
                      />
                    </div>
                    <div className="flex justify-center">
                      <Toggle
                        on={locked ? true : !!p.push}
                        locked={locked}
                        onClick={() => toggleCategoryChannel(c.key, "push", locked)}
                        disabled={catSaving}
                        testId={`notif-cat-push-${c.key}`}
                      />
                    </div>
                  </div>
                );
              })}
              <p className="text-[11px] text-gray-500 mt-2">
                Push notifications are queued for when we roll out mobile push.
                Your choice will apply automatically the moment push is enabled.
              </p>
            </div>
          )}
        </section>

        {/* Legacy email toggles */}
        <section data-testid="notif-email-section">
          <h2 className="text-lg font-black text-white mb-3">Email</h2>
          {emailLoading ? (
            <div className="text-center text-gray-400 py-6" data-testid="settings-loading">Loading…</div>
          ) : (
            <div className="space-y-3" data-testid="settings-list">
              {EMAIL_TOGGLES.map((t) => (
                <div
                  key={t.key}
                  className="flex items-center justify-between gap-4 p-4 rounded-2xl bg-[#11111a] border border-purple-500/20"
                  data-testid={`pref-row-${t.key}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-white font-bold" data-testid={`pref-label-${t.key}`}>
                      {t.label}
                    </p>
                    <p className="text-sm text-gray-400 mt-1">{t.description}</p>
                  </div>
                  <Toggle
                    on={!!emailPrefs[t.key]}
                    onClick={() => toggleEmailPref(t.key)}
                    disabled={emailSavingKey === t.key}
                    testId={`pref-toggle-${t.key}`}
                  />
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default NotificationSettingsPage;
